import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import SiteNav from "../components/Layout/SiteNav";
import useHalalFeature from "../hooks/useHalalFeature";
import {
  BRAND,
  EDITORIAL_FONT,
  PatternTile,
  Eyebrow,
  EditorialShell,
  EditorialCard,
  SerifHeading,
  PillButton,
  useEditorialPalette,
} from "../components/editorial/EditorialPrimitives";

const PILLARS = [
  {
    n: "01",
    title: "Prayer times",
    body: "Calculated for your exact location using four major calculation methods. Add widgets to your home and lock screen and see prayer times for the day at a glance.",
    ar: "الصلاة",
  },
  {
    n: "02",
    title: "Masajid",
    body: "Open directions, see iqamah times, read announcements, and follow your home masjid. Over a thousand listings, growing weekly.",
    ar: "المسجد",
  },
  {
    n: "03",
    title: "Events",
    body: "Fundraisers, halaqas, Eid prayers, and youth nights. Synced directly from each masajid calendar, with no scraping and no guesswork.",
    ar: "الفعاليات",
  },
  {
    n: "04",
    title: "Halal",
    body: "Every restaurant and market is manually certified. Real menus, owner posts, and short video, before you walk through the door.",
    ar: "حلال",
  },
];

const PROMISES = [
  {
    q: "Why free?",
    a: "The ummah built this. We pay for servers through optional Pro plans for masjid administrators, never by selling your location or attention.",
  },
  {
    q: "Why manually verified?",
    a: "An automated \"halal\" tag is worth nothing. Every restaurant uploads their certification; a human reviews it before going live. Every listing.",
  },
  {
    q: "Why so specific?",
    a: "A Muslim in Portland doesn't need a generic \"Muslim app.\" They need their masjid's iqamah, their local halal butcher, their community's events.",
  },
];

export default function Home() {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();
  const palette = useEditorialPalette();
  // Global halal feature flag: hides halal entry points while halal is off
  const { halalEnabled } = useHalalFeature();

  const isPendingMasjidAdmin =
    user &&
    userProfile?.role === "masjid" &&
    (userProfile?.pendingMasjidApproval === true ||
      !userProfile?.isAdmin ||
      (!userProfile?.masjidId && !userProfile?.activeMasjidId));

  const isIncompleteHalalUser =
    user &&
    userProfile?.role === "halal" &&
    userProfile?.restaurantProfileComplete === false;

  const hasDashboardAccess =
    !isPendingMasjidAdmin &&
    !isIncompleteHalalUser &&
    (userProfile?.role === "super" ||
      userProfile?.role === "halal" ||
      userProfile?.isAdmin);

  const getDashboardRoute = () => {
    if (!user) return "/login";
    if (isPendingMasjidAdmin) return "/masjid-signup";
    if (isIncompleteHalalUser) return "/halal-signup";
    if (userProfile?.role === "super") return "/admin";
    if (userProfile?.role === "halal") return "/halal";
    if (userProfile?.isAdmin) return "/masjid-dashboard";
    return null;
  };

  const handleRegisterMasjid = () => {
    if (authLoading) return;
    if (user && userProfile) {
      if (isPendingMasjidAdmin) return navigate("/masjid-signup");
      if (isIncompleteHalalUser) return navigate("/halal-signup");
      if (hasDashboardAccess) {
        return navigate(getDashboardRoute() || "/masjid-dashboard");
      }
      return navigate("/settings");
    }
    navigate("/signup");
  };

  const handleListHalal = () => {
    if (authLoading) return;
    if (user && userProfile) {
      if (isIncompleteHalalUser) return navigate("/halal-signup");
      if (userProfile?.role === "halal") return navigate("/halal");
    }
    navigate("/signup");
  };

  return (
    <EditorialShell>
      <SiteNav />

      {/* ══════════════════════════════════
          HERO
          ══════════════════════════════════ */}
      <section className="relative overflow-hidden" style={{ background: palette.ivory }}>
        <PatternTile opacity={0.05} />
        <div
          className="relative mx-auto px-4 pt-16 pb-12 sm:px-6 sm:pt-20 sm:pb-16 lg:px-10 lg:pt-24 lg:pb-20"
          style={{ maxWidth: 1320 }}
        >
          <div className="text-center">
            <SerifHeading
              level={1}
              className="mb-7"
              style={{
                fontSize: "clamp(2.75rem, 7vw, 6.5rem)",
                lineHeight: 0.94,
                letterSpacing: "-0.035em",
              }}
            >
              Everything the
              <br />
              ummah needs,
              <br />
              <em style={{ color: BRAND.gold, fontWeight: 400 }}>in one quiet place.</em>
            </SerifHeading>
            <p
              className="mb-8 mx-auto max-w-xl text-base sm:text-lg md:text-xl"
              style={{ color: palette.ink2, lineHeight: 1.5 }}
            >
              Muslim+ brings together prayer times, your local masajid, community events, and
              certified halal discovery. Verified, ad-free, and built by Muslims for Muslims.
            </p>
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
              <PillButton asChild variant="primary">
                <a
                  href="https://apps.apple.com/us/app/muslim-prayer-companion/id6745054019"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download for iOS
                  <span aria-hidden>→</span>
                </a>
              </PillButton>
              <PillButton asChild variant="ghost">
                <a
                  href="https://play.google.com/store/apps/details?id=com.psu.pdxmsa&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get it on Android
                </a>
              </PillButton>
            </div>
          </div>
        </div>

      </section>

      {/* ══════════════════════════════════
          FOUR PILLARS
          ══════════════════════════════════ */}
      <section style={{ background: palette.paper }}>
        <div
          className="mx-auto px-4 py-20 sm:px-6 sm:py-24 lg:px-10"
          style={{ maxWidth: 1320 }}
        >
          <div className="mb-14 grid gap-10 lg:grid-cols-[1fr_1.5fr] lg:gap-16">
            <div className="text-center lg:text-left">
              <Eyebrow>What's inside</Eyebrow>
              <SerifHeading
                className="mt-5"
                style={{
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                  letterSpacing: "-0.03em",
                }}
              >
                Four tools.
                <br />
                <em style={{ color: BRAND.gold, fontWeight: 400 }}>One deen.</em>
              </SerifHeading>
            </div>
            <div className="self-end pb-2 text-center lg:text-left">
              <p
                className="mx-auto max-w-xl text-base sm:text-lg lg:mx-0"
                style={{ color: palette.ink2, lineHeight: 1.55 }}
              >
                Muslim+ is built around the features that matter most: simple, useful, and part
                of everyday Muslim life. From finding halal places to staying connected with
                local masjids and prayer times, every feature is designed to help Muslims move
                through their day with ease.
              </p>
            </div>
          </div>

          <div
            className="grid grid-cols-1 border-t md:grid-cols-2 lg:grid-cols-4"
            style={{ borderColor: palette.hairline }}
          >
            {PILLARS.map((p, i) => (
              <div
                key={p.n}
                className="relative px-6 py-9 sm:px-7"
                style={{
                  borderRight:
                    i % 2 === 0
                      ? `1px solid ${palette.hairline}`
                      : "none",
                  borderBottom: `1px solid ${palette.hairline}`,
                }}
              >
                <div className="mb-4 flex items-baseline justify-between gap-3">
                  <SerifHeading level={3} style={{ fontSize: 26, fontWeight: 500, lineHeight: 1.1 }}>
                    {p.title}
                  </SerifHeading>
                  {p.title === "Halal" && !halalEnabled && (
                    <span
                      className="rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase"
                      style={{
                        letterSpacing: "0.12em",
                        background: palette.hairline,
                        color: palette.muted,
                      }}
                    >
                      Coming soon
                    </span>
                  )}
                  <span
                    className="text-2xl"
                    style={{
                      fontFamily: EDITORIAL_FONT.arabic,
                      color: BRAND.gold,
                      direction: "rtl",
                      lineHeight: 1,
                    }}
                  >
                    {p.ar}
                  </span>
                </div>
                <p
                  className="text-sm"
                  style={{ color: palette.ink2, lineHeight: 1.6 }}
                >
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          THE PROMISE
          ══════════════════════════════════ */}
      <section
        className="relative overflow-hidden"
        style={{ background: palette.bandBg, color: palette.bandText }}
      >
        <PatternTile opacity={0.04} color={BRAND.gold} />
        <div
          className="relative mx-auto px-4 py-20 sm:px-6 sm:py-24 lg:px-10 lg:py-28"
          style={{ maxWidth: 1080 }}
        >
          <Eyebrow color={BRAND.gold}>Our promise</Eyebrow>
          <SerifHeading
            className="mt-7"
            style={{
              fontSize: "clamp(2rem, 5.5vw, 5rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.03em",
            }}
          >
            No ads. No selling your data.
            <br />
            <em style={{ color: BRAND.gold, fontWeight: 400 }}>
              A simple way to stay connected.
            </em>
          </SerifHeading>

          <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-12">
            {PROMISES.map((item, i) => (
              <div key={i}>
                <div
                  className="mb-4"
                  style={{
                    fontFamily: EDITORIAL_FONT.serif,
                    fontStyle: "italic",
                    fontSize: 22,
                    color: BRAND.gold,
                  }}
                >
                  {item.q}
                </div>
                <p
                  className="text-[15px]"
                  style={{ color: palette.bandMuted, lineHeight: 1.6 }}
                >
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          PRODUCT SURFACES
          ══════════════════════════════════ */}
      <section style={{ background: palette.ivory }}>
        <div
          className="mx-auto px-4 py-20 sm:px-6 sm:py-24 lg:px-10"
          style={{ maxWidth: 1320 }}
        >
          <div className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <Eyebrow>Everywhere you need it</Eyebrow>
              <SerifHeading
                className="mt-5"
                style={{
                  fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
                  lineHeight: 1.02,
                }}
              >
                Pocket, browser, and
                <br />
                the TV on the back wall.
              </SerifHeading>
            </div>
            <p
              className="max-w-xs text-sm md:text-right"
              style={{ color: palette.muted }}
            >
              The same source of truth across every surface your community touches.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-6">
            {/* Phone card */}
            <EditorialCard variant="paper" className="flex flex-col justify-between overflow-hidden p-8 sm:p-10" style={{ minHeight: 440 }}>
              <div>
                <div className="mb-2 text-xs uppercase" style={{ letterSpacing: "0.14em", color: palette.muted }}>
                  Mobile
                </div>
                <SerifHeading level={3} style={{ fontSize: 30, fontWeight: 500, lineHeight: 1 }}>
                  iOS & Android
                </SerifHeading>
              </div>
              <div
                className="mx-auto my-8"
                style={{
                  width: 160,
                  height: 240,
                  background: BRAND.ink,
                  borderRadius: 28,
                  padding: 8,
                  boxShadow: "0 20px 60px -20px rgba(0,0,0,0.3)",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(180deg, ${BRAND.ink} 0%, ${BRAND.goldDeep} 100%)`,
                    borderRadius: 22,
                    padding: 14,
                    color: BRAND.ivory,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div style={{ height: 8 }} />
                  <div style={{ fontFamily: EDITORIAL_FONT.serif, fontSize: 22, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
                    As-Salāmu
                    <br />
                    ʿalaykum
                  </div>
                  <div style={{ opacity: 0.6, fontSize: 9 }}>Next · Dhuhr in 32m</div>
                  <div style={{ marginTop: "auto", display: "flex", gap: 4 }}>
                    {[0, 1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        style={{
                          flex: 1,
                          height: 28,
                          background: i === 1 ? BRAND.gold : "#ffffff15",
                          borderRadius: 4,
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </EditorialCard>

            {/* Web card */}
            <EditorialCard variant="ink" className="flex flex-col justify-between overflow-hidden p-8 sm:p-10" style={{ minHeight: 440 }}>
              <div>
                <div className="mb-2 text-xs uppercase" style={{ letterSpacing: "0.14em", color: "#9a9288" }}>
                  Web
                </div>
                <SerifHeading level={3} style={{ fontSize: 30, fontWeight: 500, lineHeight: 1 }}>
                  Embeddable widget
                </SerifHeading>
              </div>
              <div
                className="my-6 rounded-xl p-5 text-[12px]"
                style={{
                  background: BRAND.ivory,
                  color: BRAND.ink,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                <div className="mb-2" style={{ color: BRAND.muted }}>
                  {"<!-- your site -->"}
                </div>
                <div>
                  {"<iframe src="}
                  <span style={{ color: BRAND.gold }}>"muslim.plus/</span>
                </div>
                <div className="pl-3.5" style={{ color: BRAND.gold }}>
                  widget/prayer/as-sabr"
                </div>
                <div>{"/>"}</div>
              </div>
              <div className="text-[13px]" style={{ color: "#c8bfa8" }}>
                Drop prayer times straight into your masjid's website. Two lines of code.
              </div>
            </EditorialCard>

            {/* TV card */}
            <EditorialCard variant="paper" className="flex flex-col justify-between overflow-hidden p-8 sm:p-10" style={{ minHeight: 440 }}>
              <div>
                <div className="mb-2 text-xs uppercase" style={{ letterSpacing: "0.14em", color: palette.muted }}>
                  Display
                </div>
                <SerifHeading level={3} style={{ fontSize: 30, fontWeight: 500, lineHeight: 1 }}>
                  TV views for the masjid
                </SerifHeading>
              </div>
              <div
                className="my-6 flex flex-col items-center justify-center rounded-xl px-4 py-10"
                style={{ background: BRAND.ink, color: BRAND.ivory, aspectRatio: "16/10" }}
              >
                <div
                  className="mb-1 text-xl"
                  style={{ fontFamily: EDITORIAL_FONT.arabic, color: BRAND.gold, direction: "rtl" }}
                >
                  الظهر
                </div>
                <div style={{ fontFamily: EDITORIAL_FONT.serif, fontSize: 44, letterSpacing: "-0.02em", lineHeight: 1 }}>
                  1:15 PM
                </div>
                <div
                  className="mt-2 text-[9px] uppercase"
                  style={{ letterSpacing: "0.12em", color: "#c8bfa8" }}
                >
                  NEXT · IQAMAH IN 32 MIN
                </div>
              </div>
              <div className="text-[13px]" style={{ color: palette.ink2 }}>
                One URL. Any browser. Auto-updates, announcements, and events rotate on the hour.
              </div>
            </EditorialCard>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          MUSLIM+ SOLUTIONS
          ══════════════════════════════════ */}
      <section style={{ background: palette.paper, borderTop: `1px solid ${palette.hairline}` }}>
        <div
          className="mx-auto grid gap-10 px-4 py-20 sm:px-6 sm:py-24 lg:grid-cols-[1.2fr_1fr] lg:items-center lg:gap-16 lg:px-10"
          style={{ maxWidth: 1320 }}
        >
          <div>
            <Eyebrow>Muslim+ Solutions</Eyebrow>
            <SerifHeading
              className="mt-5 mb-6"
              style={{
                fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
                lineHeight: 1.02,
                letterSpacing: "-0.03em",
              }}
            >
              Software for your business,
              <br />
              <em style={{ color: BRAND.gold, fontWeight: 400 }}>
                built by the same team.
              </em>
            </SerifHeading>
            <p
              className="mb-8 max-w-xl text-base sm:text-lg"
              style={{ color: palette.ink2, lineHeight: 1.55 }}
            >
              Custom websites, apps, and software for local businesses. A one
              time down payment, then a monthly support subscription. We build
              it, host it, and keep it running.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <PillButton variant="primary" onClick={() => navigate("/solutions")}>
                Explore Solutions
                <span aria-hidden>→</span>
              </PillButton>
              <PillButton variant="ghost" onClick={() => navigate("/solutions/portal")}>
                Customer portal
              </PillButton>
            </div>
          </div>
          <EditorialCard
            variant="ink"
            className="p-8 sm:p-10"
            style={{ borderLeft: `3px solid ${BRAND.gold}`, borderRadius: 20 }}
          >
            <div
              className="mb-4 text-xs uppercase"
              style={{ letterSpacing: "0.14em", color: "#9a9288" }}
            >
              What we build
            </div>
            <div className="flex flex-col gap-4">
              {[
                "Websites for restaurants, shops, and practices",
                "Mobile apps for ordering and booking",
                "Custom software and internal tools",
                "E-commerce with payments built in",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <span
                    aria-hidden
                    style={{
                      color: BRAND.gold,
                      fontFamily: EDITORIAL_FONT.serif,
                      lineHeight: 1.4,
                    }}
                  >
                    →
                  </span>
                  <span className="text-[15px]" style={{ color: "#c8bfa8", lineHeight: 1.5 }}>
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </EditorialCard>
        </div>
      </section>

      {/* ══════════════════════════════════
          CTA
          ══════════════════════════════════ */}
      <section style={{ background: palette.ivory, borderTop: `1px solid ${palette.hairline}` }}>
        <div
          className="mx-auto grid gap-12 px-4 py-20 sm:px-6 sm:py-24 lg:grid-cols-2 lg:items-center lg:gap-16 lg:px-10"
          style={{ maxWidth: 1320 }}
        >
          <div>
            <Eyebrow>Start today</Eyebrow>
            <SerifHeading
              className="mt-5 mb-8"
              style={{
                fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)",
                lineHeight: 0.98,
                letterSpacing: "-0.03em",
              }}
            >
              Install it once.
              <br />
              <em style={{ color: BRAND.gold, fontWeight: 400 }}>Use it forever.</em>
            </SerifHeading>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <PillButton asChild variant="primary">
                <a
                  href="https://apps.apple.com/us/app/muslim-prayer-companion/id6745054019"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download for iOS
                  <span aria-hidden>→</span>
                </a>
              </PillButton>
              <PillButton asChild variant="ghost">
                <a
                  href="https://play.google.com/store/apps/details?id=com.psu.pdxmsa&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get it on Android
                </a>
              </PillButton>
              <PillButton variant="outline" onClick={() => navigate("/masjids")}>
                Browse masajid
              </PillButton>
            </div>
          </div>
          <EditorialCard
            variant="paper"
            className="p-8 sm:p-10"
            style={{ borderLeft: `3px solid ${BRAND.gold}`, borderRadius: 20 }}
          >
            <div
              className="mb-4 text-xs uppercase"
              style={{ letterSpacing: "0.14em", color: palette.muted }}
            >
              {halalEnabled ? "For masjid & halal partners" : "For masjid partners"}
            </div>
            <SerifHeading level={3} className="mb-4" style={{ fontSize: 28, fontWeight: 500, lineHeight: 1.15 }}>
              {halalEnabled
                ? "List your masjid or halal business, free."
                : "List your masjid, free."}
            </SerifHeading>
            <p className="mb-6 text-[15px]" style={{ color: palette.ink2, lineHeight: 1.55 }}>
              Verified listings, real-time prayer control, announcements, and event publishing.
              Pro features for $25/month. Free forever tier. No catch.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <PillButton variant="primary" onClick={handleRegisterMasjid}>
                Register masjid
                <span aria-hidden>→</span>
              </PillButton>
              {halalEnabled ? (
                <PillButton variant="ghost" onClick={handleListHalal}>
                  List halal business
                </PillButton>
              ) : (
                <PillButton variant="ghost" disabled>
                  Halal listings coming soon
                </PillButton>
              )}
            </div>
          </EditorialCard>
        </div>
      </section>

      {/* ══════════════════════════════════
          FOOTER
          ══════════════════════════════════ */}
      <footer
        className="relative"
        style={{ background: palette.paper, borderTop: `1px solid ${palette.hairline}`, color: palette.ink }}
      >
        <div className="container mx-auto px-4 py-16 sm:px-6 lg:px-10">
          <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
            <div className="lg:col-span-2">
              <div className="mb-4 flex items-center gap-2.5">
                <span
                  style={{
                    fontFamily: EDITORIAL_FONT.serif,
                    fontSize: 24,
                    fontWeight: 600,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Muslim<span style={{ color: BRAND.gold }}>+</span>
                </span>
              </div>
              <p
                className="mb-6 max-w-md text-[15px] leading-relaxed"
                style={{ color: palette.muted }}
              >
                A quiet, complete utility for the ummah. Prayer times, your local masajid,
                community events, and certified halal, all in one place, ad-free, forever.
              </p>
            </div>

            <div>
              <div
                className="mb-5 text-[11px] font-semibold uppercase"
                style={{ letterSpacing: "0.16em", color: palette.muted }}
              >
                Product
              </div>
              <div className="flex flex-col gap-3 text-sm">
                <button onClick={() => navigate("/masjids")} className="text-left transition-colors hover:text-[#bf9a2f]">
                  Find Masajid
                </button>
                <button onClick={() => navigate("/events-public")} className="text-left transition-colors hover:text-[#bf9a2f]">
                  Browse Events
                </button>
                {halalEnabled && (
                  <button onClick={() => navigate("/halal-info")} className="text-left transition-colors hover:text-[#bf9a2f]">
                    Halal Finder
                  </button>
                )}
                <button onClick={() => navigate("/masjid-info")} className="text-left transition-colors hover:text-[#bf9a2f]">
                  For Masajid
                </button>
                <button onClick={() => navigate("/solutions")} className="text-left transition-colors hover:text-[#bf9a2f]">
                  Solutions
                </button>
              </div>
            </div>

            <div>
              <div
                className="mb-5 text-[11px] font-semibold uppercase"
                style={{ letterSpacing: "0.16em", color: palette.muted }}
              >
                Company
              </div>
              <div className="flex flex-col gap-3 text-sm">
                {user && userProfile ? (
                  <button
                    onClick={() => {
                      if (isPendingMasjidAdmin) return navigate("/masjid-signup");
                      if (isIncompleteHalalUser) return navigate("/halal-signup");
                      const dashboardRoute = getDashboardRoute();
                      navigate(dashboardRoute || "/settings");
                    }}
                    className="text-left transition-colors hover:text-[#bf9a2f]"
                  >
                    {isPendingMasjidAdmin || isIncompleteHalalUser
                      ? "Complete Signup"
                      : hasDashboardAccess
                      ? "Dashboard"
                      : "Settings"}
                  </button>
                ) : (
                  <>
                    <button onClick={() => navigate("/login")} className="text-left transition-colors hover:text-[#bf9a2f]">
                      Sign In
                    </button>
                    <button onClick={() => navigate("/signup")} className="text-left transition-colors hover:text-[#bf9a2f]">
                      Sign Up
                    </button>
                  </>
                )}
                <a
                  href="https://www.instagram.com/muslimplus.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-[#bf9a2f]"
                >
                  Instagram
                </a>
                <button onClick={() => navigate("/policy")} className="text-left transition-colors hover:text-[#bf9a2f]">
                  Privacy Policy
                </button>
              </div>
            </div>
          </div>

          <div
            className="mt-12 flex flex-col items-start justify-between gap-3 border-t pt-8 text-xs md:flex-row md:items-center"
            style={{ borderColor: palette.hairline, color: palette.muted }}
          >
            <span>© {new Date().getFullYear()} Muslim+ · LLC</span>
            <span>
              Made with ❤️ for the Muslim community
            </span>
          </div>
        </div>
      </footer>
    </EditorialShell>
  );
}
